/** Layer-2 active relation highlight — direct neighbors only (distance = 1).
 * Directional flow colors are derived from selectedNodeId + edge topology,
 * never persisted and never based on screen position. */

export interface CanvasEdgeRef {
  id: string
  source: string
  target: string
}

export type SelectedNodeEdgeRelation = 'inbound' | 'outbound' | 'unrelated' | 'self-loop'

/** Pure resolver: graph topology only. O(1) per edge. */
export function resolveSelectedNodeEdgeRelation(
  edge: CanvasEdgeRef,
  selectedNodeId: string | null | undefined,
): SelectedNodeEdgeRelation {
  if (!selectedNodeId) return 'unrelated'
  if (edge.source === selectedNodeId && edge.target === selectedNodeId) return 'self-loop'
  if (edge.source === selectedNodeId) return 'outbound'
  if (edge.target === selectedNodeId) return 'inbound'
  return 'unrelated'
}

export function computeDirectNeighborEdgeIds(
  selectedNodeId: string | null | undefined,
  edges: CanvasEdgeRef[],
): Set<string> {
  if (!selectedNodeId) return new Set()
  const ids = new Set<string>()
  for (const edge of edges) {
    if (edge.source === selectedNodeId || edge.target === selectedNodeId) {
      ids.add(edge.id)
    }
  }
  return ids
}

export function resolveEdgeDisplayClassFromRelation(relation: SelectedNodeEdgeRelation): string {
  switch (relation) {
    case 'inbound':
      return 'pcv-edge pcv-edge--flow-inbound'
    case 'outbound':
      return 'pcv-edge pcv-edge--flow-outbound'
    case 'self-loop':
    case 'unrelated':
    default:
      return 'pcv-edge'
  }
}

export function resolveEdgeDisplayClass(
  edge: CanvasEdgeRef,
  selectedNodeId: string | null | undefined,
): string {
  return resolveEdgeDisplayClassFromRelation(resolveSelectedNodeEdgeRelation(edge, selectedNodeId))
}
